import React, { useEffect, useMemo, useState, useRef, Fragment, createElement, Component } from 'react';
import { useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { DownloadIcon, CalendarIcon, ChevronRightIcon, ChevronDownIcon, FileTextIcon, InfoIcon, SearchIcon, XIcon } from 'lucide-react';
import { getTenant, formatCurrencyAmount } from '../../../theme/tokens';
import { InfoTooltip } from '../../../shared/ui/InfoTooltip';
import { useCompany } from '../../../app/providers/CompanyProvider';
import { getAllPayouts, type Payout, type PayoutTransfer, type PayoutStatus, type TransferStatus } from '../mocks/payoutsData';
const PAGE_SIZE = 9;
// Merchant-facing payout description in the format institution clients use:
// "Payout DDMMYYYY" derived from the payout's effective date.
const formatPayoutDescription = (p: Payout) => {
  const d = p.effectiveDateRaw;
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  return `Payout ${dd}${mm}${yyyy}`;
};
const formatCurrency = (amount: number) => {
  return formatCurrencyAmount(amount, {
    fractionDigits: 2
  });
};
// Amount without the currency code — used in the table's Gross / Deductions /
// Net columns, since the row already has a dedicated Currency column.
const formatAmount = (amount: number) => new Intl.NumberFormat(undefined, {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2
}).format(amount);
// Signed amount for the Net adjustments column: explicit + / − so the sign
// of the total adjustment is unambiguous.
const formatSignedAmount = (amount: number) => {
  const sign = amount > 0 ? '+' : amount < 0 ? '−' : '';
  return `${sign}${formatAmount(Math.abs(amount))}`;
};
function getPayoutStatusBadge(status: PayoutStatus) {
  const styles: Record<PayoutStatus, string> = {
    submitted: 'bg-[var(--colors-info-light)] text-[var(--colors-info-dark)] border-[var(--colors-info-default)]',
    partially_submitted: 'bg-[var(--colors-warning-light)] text-[var(--colors-warning-dark)] border-[var(--colors-warning-default)]',
    failed: 'bg-[var(--colors-error-light)] text-[var(--colors-error-dark)] border-[var(--colors-error-default)]',
    cancelled: 'bg-[var(--colors-neutral-50)] text-[var(--colors-neutral-600)] border-[var(--colors-neutral-200)]',
    pending: 'bg-[var(--colors-neutral-50)] text-[var(--colors-neutral-600)] border-[var(--colors-neutral-200)]'
  };
  const labels: Record<PayoutStatus, string> = {
    submitted: 'Submitted',
    partially_submitted: 'Partially submitted',
    failed: 'Failed',
    cancelled: 'Cancelled',
    pending: 'Pending'
  };
  return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${styles[status]}`} data-id="element-3229" data-mp="1d2563kse1azb">
      {labels[status]}
    </span>;
}
function getTransferStatusBadge(status: TransferStatus) {
  const styles: Record<TransferStatus, string> = {
    submitted: 'bg-[var(--colors-info-light)] text-[var(--colors-info-dark)] border-[var(--colors-info-default)]',
    failed: 'bg-[var(--colors-error-light)] text-[var(--colors-error-dark)] border-[var(--colors-error-default)]',
    cancelled: 'bg-[var(--colors-neutral-50)] text-[var(--colors-neutral-600)] border-[var(--colors-neutral-200)]',
    pending: 'bg-[var(--colors-neutral-50)] text-[var(--colors-neutral-600)] border-[var(--colors-neutral-200)]'
  };
  const labels: Record<TransferStatus, string> = {
    submitted: 'Submitted',
    failed: 'Failed',
    cancelled: 'Cancelled',
    pending: 'Pending'
  };
  return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${styles[status]}`} data-id="element-3230" data-mp="1aoji4ygcxfbo">
      {labels[status]}
    </span>;
}
// Shared CSV escape utility
function escCsv(val: string | number): string {
  const s = String(val);
  if (s.includes(',') || s.includes('"') || s.includes('\n')) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}
function downloadBlob(filename: string, csvContent: string) {
  const blob = new Blob([csvContent], {
    type: 'text/csv;charset=utf-8;'
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
// ─── Report 1: Payout Summary (one row per payout) ───
function downloadPayoutSummaryCsv(payouts: Payout[], merchantName: string) {
  const headers = ['Merchant name', 'Merchant ID', 'Payout ID', 'Payout status', 'Payout date', 'Currency', 'Gross amount', 'Net adjustments', 'Net payout', 'Destination account', 'Transfers', 'Payout description', 'Bank payout reference'];
  const rows = payouts.map(p => {
    // The payout-level bank reference is the shared reference across all of
    // the payout's transfers; transfer rows carry per-transfer variants (with
    // a "-NN" index suffix), so strip that suffix to get the payout reference.
    const first = p.transfers[0];
    const bankPayoutReference = first ? first.bankReference.replace(/-\d{2}$/, '') : '';
    return [escCsv(merchantName), '', escCsv(p.payoutId), escCsv(p.status), escCsv(p.effectiveDate), escCsv(p.currency), p.grossAmount.toFixed(2), p.netAdjustments.toFixed(2), p.netAmount.toFixed(2), escCsv(p.destinationAccount), p.transfers.length, escCsv(formatPayoutDescription(p)), escCsv(bankPayoutReference)].join(',');
  });
  const brandName = getTenant().brand.name.toLowerCase();
  downloadBlob(`${brandName}-payout-summary-${new Date().toISOString().slice(0, 10)}.csv`, '\uFEFF' + [headers.join(','), ...rows].join('\n'));
}
// ─── Report 2: Payout Detail Report (one row per transaction line item) ───
interface PayoutTransaction {
  activityType: 'Payment' | 'Refund' | 'Fee' | 'Chargeback' | 'Adjustment';
  sourceActivityId: string;
  currency: string;
  grossAmount: number;
  fee: number;
  netAmount: number;
  description: string;
  orderId: string;
  merchantReferenceId: string;
}
function generateMockTransactions(payout: Payout): PayoutTransaction[] {
  const seed = payout.payoutId.split('').reduce((acc, c) => acc + c.charCodeAt(0), 0);
  const rng = (i: number) => {
    const x = Math.sin(seed + i) * 10000;
    return x - Math.floor(x);
  };
  const txns: PayoutTransaction[] = [];
  let remainingGross = payout.grossAmount;
  // Fees flowing into the per-transaction detail = the fee + VAT adjustments
  // (negative), offset by the WHT adjustment (positive).
  const feeAndVat = payout.adjustments.filter(a => a.kind === 'fees' || a.kind === 'vat_on_fee').reduce((s, a) => s + a.amount, 0);
  const wht = payout.adjustments.filter(a => a.kind === 'wht_adjustment').reduce((s, a) => s + a.amount, 0);
  let remainingFees = Math.abs(feeAndVat) - wht;
  const descs = ['Weekly grocery order - fresh produce', 'Bulk rice and noodles purchase', 'Seafood platter catering order', 'Office lunch delivery - 25 pax', 'Monthly subscription box - premium', 'Fruit basket gift set x3', 'Restaurant supply restock', 'Organic vegetables wholesale', 'Frozen goods monthly order', 'Beverage supply - soft drinks', 'Bakery ingredients wholesale', 'Meat and poultry weekly supply', 'Dairy products - milk and cheese', 'Cooking oil and condiments restock', 'Hotel breakfast buffet supply', 'School canteen weekly provisions'];
  const count = 8 + Math.floor(rng(0) * 8);
  const base = payout.grossAmount / count;
  for (let i = 0; i < count; i++) {
    const isLast = i === count - 1;
    const gross = isLast ? remainingGross : Math.round(base * (0.5 + rng(i + 10)) * 100) / 100;
    const actualGross = Math.max(100, Math.min(gross, remainingGross));
    const fee = isLast ? remainingFees : Math.round(actualGross * (0.029 + rng(i + 50) * 0.005) * 100) / 100;
    remainingGross = Math.round((remainingGross - actualGross) * 100) / 100;
    remainingFees = Math.round((remainingFees - fee) * 100) / 100;
    const num = String(10000 + Math.floor(rng(i + 20) * 90000));
    txns.push({
      activityType: 'Payment',
      sourceActivityId: `pay_${num}`,
      currency: payout.currency,
      grossAmount: actualGross,
      fee,
      netAmount: Math.round((actualGross - fee) * 100) / 100,
      description: descs[Math.floor(rng(i + 60) * descs.length)],
      orderId: `ord_${String(10000 + Math.floor(rng(i + 30) * 90000))}`,
      merchantReferenceId: `MR-${String(1000 + Math.floor(rng(i + 40) * 9000))}`
    });
  }
  // Add 1-2 refunds
  const refundCount = 1 + Math.floor(rng(100) * 2);
  for (let i = 0; i < refundCount; i++) {
    const amt = Math.round((500 + rng(i + 200) * 5000) * 100) / 100;
    const num = String(10000 + Math.floor(rng(i + 210) * 90000));
    txns.push({
      activityType: 'Refund',
      sourceActivityId: `rf_${num}`,
      currency: payout.currency,
      grossAmount: -amt,
      fee: 0,
      netAmount: -amt,
      description: 'Refund - customer return',
      orderId: txns[Math.floor(rng(i + 220) * Math.min(3, txns.length))]?.orderId || `ord_${num}`,
      merchantReferenceId: txns[0]?.merchantReferenceId || `MR-${num}`
    });
  }
  return txns;
}
// Exact detail-row count for a filtered set (used for the export estimate).
function countPayoutDetailRows(payouts: Payout[]): number {
  return payouts.reduce((sum, p) => sum + generateMockTransactions(p).length, 0);
}
function downloadPayoutDetailCsv(payouts: Payout[]) {
  const headers = ['Payout ID', 'Activity Type', 'Source Activity ID', 'Currency', 'Gross Amount', 'Fee', 'Net Amount', 'Description', 'Order ID', 'Merchant Reference ID'];
  const rows: string[] = [];
  payouts.forEach(p => {
    const txns = generateMockTransactions(p);
    txns.forEach(t => {
      rows.push([escCsv(p.payoutId), escCsv(t.activityType), escCsv(t.sourceActivityId), escCsv(t.currency), t.grossAmount.toFixed(2), t.fee.toFixed(2), t.netAmount.toFixed(2), escCsv(t.description), escCsv(t.orderId), escCsv(t.merchantReferenceId)].join(','));
    });
  });
  const brandName = getTenant().brand.name.toLowerCase();
  downloadBlob(`${brandName}-payout-detail-${new Date().toISOString().slice(0, 10)}.csv`, '\uFEFF' + [headers.join(','), ...rows].join('\n'));
}
// ─── Report Dropdown Component ───
function ReportDropdown({
  label,
  onSummary,
  onDetail
}: {
  label: string;
  onSummary: () => void;
  onDetail: () => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);
  return <div ref={ref} className="relative inline-block" data-id="element-3231" data-mp="vufcy43bh7l1">
      <button onClick={() => setOpen(!open)} className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-gray-600 bg-white border border-gray-200 rounded-md hover:bg-gray-50 transition-colors" data-id="element-3232" data-mp="19v9k6jemp2i4">
        <DownloadIcon size={12} data-id="element-3233" data-mp="1jl2wxnqibnny" />
        {label}
        <ChevronDownIcon size={10} data-id="element-3234" data-mp="1emro9gby7j7b" />
      </button>

      <AnimatePresence data-id="element-3235" data-mp="1en1dv2t2zoch">
        {open && <motion.div initial={{
        opacity: 0,
        y: -4
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -4
      }} transition={{
        duration: 0.12
      }} className="absolute right-0 mt-1 w-56 bg-white border border-gray-200 rounded-lg shadow-lg z-50 py-1" data-id="element-3236" data-mp="1tjin9ohpmswe">
            <button onClick={() => {
          onSummary();
          setOpen(false);
        }} className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2.5" data-id="element-3237" data-mp="25jytxpkiver0">
              <FileTextIcon size={14} className="text-gray-400" data-id="element-3238" data-mp="2jcf2t703u6zu" />
              <div data-id="element-3239" data-mp="2ocszxu063wj3">
                <div className="font-medium" data-id="element-3240" data-mp="2oc9sj7858qjb">Payout summary</div>
                <div className="text-xs text-gray-500" data-id="element-3241" data-mp="2tbmcnk7mrji7">One row per payout</div>
              </div>
            </button>
            <button onClick={() => {
          onDetail();
          setOpen(false);
        }} className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2.5" data-id="element-3242" data-mp="tz1sn15b9oqt">
              <FileTextIcon size={14} className="text-gray-400" data-id="element-3243" data-mp="rg6s2s68d40u" />
              <div data-id="element-3244" data-mp="mmc8rpujer2a">
                <div className="font-medium" data-id="element-3245" data-mp="mm2kc6p54ryp">Payout detail</div>
                <div className="text-xs text-gray-500" data-id="element-3246" data-mp="hnhkw9hpqcbb">
                  One row per transaction or payout detail line
                </div>
              </div>
            </button>
          </motion.div>}
      </AnimatePresence>
    </div>;
}
// ─── Filter model ──────────────────────────────────────────────────────────
type StatusFilter = 'all' | PayoutStatus;
type DatePreset = 'today' | 'last_7' | 'last_30' | 'last_90' | 'mtd' | 'ytd' | 'custom';
const STATUS_OPTIONS: {
  value: StatusFilter;
  label: string;
}[] = [{
  value: 'all',
  label: 'All'
}, {
  value: 'pending',
  label: 'Pending'
}, {
  value: 'submitted',
  label: 'Submitted'
}, {
  value: 'partially_submitted',
  label: 'Partially submitted'
}, {
  value: 'failed',
  label: 'Failed'
}, {
  value: 'cancelled',
  label: 'Cancelled'
}];
const DATE_PRESET_OPTIONS: {
  value: DatePreset;
  label: string;
}[] = [{
  value: 'today',
  label: 'Today'
}, {
  value: 'last_7',
  label: 'Last 7 days'
}, {
  value: 'last_30',
  label: 'Last 30 days'
}, {
  value: 'last_90',
  label: 'Last 90 days'
}, {
  value: 'mtd',
  label: 'Month to date'
}, {
  value: 'ytd',
  label: 'Year to date'
}, {
  value: 'custom',
  label: 'Custom range'
}];
// Start of the calendar day for a given date.
function startOfDay(d: Date): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}
function endOfDay(d: Date): Date {
  const x = new Date(d);
  x.setHours(23, 59, 59, 999);
  return x;
}
/**
 * Resolve a date preset (or custom range) into an inclusive [start, end]
 * window. The reference "today" is anchored to the most recent payout so the
 * relative presets stay meaningful against the mock data instead of the real
 * clock. Returns null when a custom range hasn't been fully specified.
 */
function resolveDateRange(preset: DatePreset, anchor: Date, customStart: string, customEnd: string): {
  start: Date;
  end: Date;
} | null {
  if (preset === 'custom') {
    if (!customStart || !customEnd) return null;
    return {
      start: startOfDay(new Date(customStart)),
      end: endOfDay(new Date(customEnd))
    };
  }
  const end = endOfDay(anchor);
  const start = new Date(anchor);
  switch (preset) {
    case 'today':
      return {
        start: startOfDay(anchor),
        end
      };
    case 'last_7':
      start.setDate(start.getDate() - 6);
      return {
        start: startOfDay(start),
        end
      };
    case 'last_30':
      start.setDate(start.getDate() - 29);
      return {
        start: startOfDay(start),
        end
      };
    case 'last_90':
      start.setDate(start.getDate() - 89);
      return {
        start: startOfDay(start),
        end
      };
    case 'mtd':
      return {
        start: startOfDay(new Date(anchor.getFullYear(), anchor.getMonth(), 1)),
        end
      };
    case 'ytd':
      return {
        start: startOfDay(new Date(anchor.getFullYear(), 0, 1)),
        end
      };
  }
}
function formatRangeLabel(range: {
  start: Date;
  end: Date;
} | null): string {
  if (!range) return 'All dates';
  const fmt = (d: Date) => d.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });
  return `${fmt(range.start)} – ${fmt(range.end)}`;
}
function StatusTooltipHeader() {
  const [isVisible, setIsVisible] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [pos, setPos] = useState({
    top: 0,
    left: 0
  });
  useEffect(() => {
    if (isVisible && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setPos({
        top: rect.bottom + 8,
        left: rect.left + rect.width / 2
      });
    }
  }, [isVisible]);
  return <div className="flex items-center gap-1.5" data-id="element-3247" data-mp="2dsten2m1aw74">
      Status
      <button ref={buttonRef} type="button" className="text-gray-400 hover:text-gray-500 focus:outline-none" onMouseEnter={() => setIsVisible(true)} onMouseLeave={() => setIsVisible(false)} onFocus={() => setIsVisible(true)} onBlur={() => setIsVisible(false)} data-id="element-3248" data-mp="28u9w7c6ng2ji">
        <InfoIcon size={13} data-id="element-3249" data-mp="1u76usuf7h9qw" />
      </button>
      {isVisible && createPortal(<div style={{
      top: pos.top,
      left: pos.left
    }} className="fixed -translate-x-1/2 w-72 p-3 bg-gray-900 text-white text-xs rounded-lg shadow-lg z-[9999] font-normal normal-case tracking-normal leading-relaxed pointer-events-none" data-id="element-3250" data-mp="1q38z6tp3ah2l">
            <p className="font-semibold mb-2 text-[11px] uppercase tracking-wider text-gray-300" data-id="element-3251" data-mp="3fi6zmcz5zw3o">
              Payout statuses
            </p>
            <ul className="space-y-1.5 mb-3" data-id="element-3252" data-mp="2jet5l9xqq3oa">
              <li data-id="element-3253" data-mp="2jf1g4v6e4wwb">
                <span className="font-medium text-white" data-id="element-3254" data-mp="2jgm9wikvsbt4">Pending</span> —
                Created but not yet submitted to the bank
              </li>
              <li data-id="element-3255" data-mp="29phwvoern10x">
                <span className="font-medium text-white" data-id="element-3256" data-mp="29nygzyx0urzo">Submitted</span> — All
                transfers submitted to the bank
              </li>
              <li data-id="element-3257" data-mp="20o1lpeg4muvn">
                <span className="font-medium text-white" data-id="element-3258" data-mp="20nidwqk6xmvc">
                  Partially submitted
                </span>{' '}
                — Some transfers submitted, others pending or failed
              </li>
              <li data-id="element-3259" data-mp="1qqnkgm2rycnx">
                <span className="font-medium text-white" data-id="element-3260" data-mp="1qr5kltsk5b14">Failed</span> —
                Submission failed
              </li>
              <li data-id="element-3261" data-mp="1vr1gf5r3pw5l">
                <span className="font-medium text-white" data-id="element-3262" data-mp="3l3vh8jab8yc7">Cancelled</span> —
                Cancelled before submission completed
              </li>
            </ul>

            <p className="font-semibold mb-2 text-[11px] uppercase tracking-wider text-gray-300" data-id="element-3263" data-mp="3q2g9nwjbmdq3">
              Transfer statuses
            </p>
            <ul className="space-y-1.5" data-id="element-3264" data-mp="19gte19u8thi2">
              <li data-id="element-3265" data-mp="19hurtm2n8i3h">
                <span className="font-medium text-white" data-id="element-3266" data-mp="19icqepcs38ek">Pending</span> —
                Created but not yet submitted
              </li>
              <li data-id="element-3267" data-mp="1jezcfhguc5i6">
                <span className="font-medium text-white" data-id="element-3268" data-mp="1jefxm9mhlx8d">Submitted</span> —
                Instruction submitted to the bank
              </li>
              <li data-id="element-3269" data-mp="1x876dfse9d31">
                <span className="font-medium text-white" data-id="element-3270" data-mp="1t4iyevzuofh9">Failed</span> —
                Submission failed
              </li>
              <li data-id="element-3271" data-mp="274tm5q5v5dy7">
                <span className="font-medium text-white" data-id="element-3272" data-mp="2738qtybq5qza">Cancelled</span> —
                Cancelled before submission
              </li>
            </ul>
          </div>, document.body)}
    </div>;
}
// ─── Export confirmation dialog ──────────────────────────────────────────
interface ExportRequest {
  type: 'summary' | 'detail';
  payouts: Payout[];
  rangeLabel: string;
  statusLabel: string;
  currencyLabel: string | null;
  destinationLabel: string | null;
  detailRowCount: number;
}
function ExportConfirmDialog({
  request,
  onCancel,
  onConfirm
}: {
  request: ExportRequest;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const typeLabel = request.type === 'summary' ? 'payout summary' : 'payout detail';
  const count = request.payouts.length;
  return createPortal(<div className="fixed inset-0 z-[9998] flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-labelledby="export-dialog-title" data-id="element-3273" data-mp="1ghwuud29huio">
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="absolute inset-0 bg-gray-900/40" onClick={onCancel} data-id="element-3274" data-mp="1qfl2g3kyuwi9" />
      <motion.div initial={{
      opacity: 0,
      scale: 0.97,
      y: 8
    }} animate={{
      opacity: 1,
      scale: 1,
      y: 0
    }} exit={{
      opacity: 0,
      scale: 0.97,
      y: 8
    }} transition={{
      duration: 0.15
    }} className="relative w-full max-w-md bg-white rounded-xl shadow-xl border border-gray-200" data-id="element-3275" data-mp="31wb4daicx262">
        <div className="flex items-start justify-between px-6 pt-5 pb-4 border-b border-gray-100" data-id="element-3276" data-mp="en2847f6s1cp">
          <h2 id="export-dialog-title" className="text-lg font-semibold text-gray-900 capitalize" data-id="element-3277" data-mp="12co82x3duklr">
            Export {typeLabel}
          </h2>
          <button onClick={onCancel} aria-label="Close" className="text-gray-400 hover:text-gray-600 transition-colors -mr-1" data-id="element-3278" data-mp="smblz0fx6jdi">
            <XIcon size={18} data-id="element-3279" data-mp="1lahc62pqt0kq" />
          </button>
        </div>
        <dl className="px-6 py-4 space-y-2.5 text-sm" data-id="element-3280" data-mp="1l8wiefb95lnx">
          <div className="flex justify-between gap-4" data-id="element-3281" data-mp="1q1plyzuzinyf">
            <dt className="text-gray-500" data-id="element-3282" data-mp="1q3aeyl1nhyu6">Date range</dt>
            <dd className="text-gray-900 font-medium text-right" data-id="element-3283" data-mp="1zxfwzeu3gwqh">
              {request.rangeLabel}
            </dd>
          </div>
          <div className="flex justify-between gap-4" data-id="element-3284" data-mp="1uyux5gir8f2k">
            <dt className="text-gray-500" data-id="element-3285" data-mp="1py7993ptoeck">Status</dt>
            <dd className="text-gray-900 font-medium text-right" data-id="element-3286" data-mp="1q1360opxowzf">
              {request.statusLabel}
            </dd>
          </div>
          {request.currencyLabel && <div className="flex justify-between gap-4" data-id="element-3287" data-mp="1gz3mnprg5gtb">
              <dt className="text-gray-500" data-id="element-3288" data-mp="1c7vcgrialrf3">Currency</dt>
              <dd className="text-gray-900 font-medium text-right" data-id="element-3289" data-mp="1c6alfbv6sqlz">
                {request.currencyLabel}
              </dd>
            </div>}
          {request.destinationLabel && <div className="flex justify-between gap-4" data-id="element-3290" data-mp="12b8ty9dfvcg8">
              <dt className="text-gray-500" data-id="element-3291" data-mp="xakvx3qslvci">Destination account</dt>
              <dd className="text-gray-900 font-medium text-right" data-id="element-3292" data-mp="t8pu7y4y9fy7">
                {request.destinationLabel}
              </dd>
            </div>}
        </dl>
        <div className="px-6 pb-4" data-id="element-3293" data-mp="34vtetfgz2ksn">
          <div className="rounded-lg bg-gray-50 border border-gray-100 px-4 py-3 text-sm" data-id="element-3294" data-mp="38xekogv39ytc">
            <div className="font-semibold text-gray-900" data-id="element-3295" data-mp="3dwrifwni0qap">
              {count} payout{count !== 1 ? 's' : ''}
            </div>
            {request.type === 'detail' && <div className="text-gray-500 mt-0.5" data-id="element-3296" data-mp="3itrpoaw33z3e">
                ~{request.detailRowCount} detail row
                {request.detailRowCount !== 1 ? 's' : ''} estimated
              </div>}
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-gray-100" data-id="element-3297" data-mp="2v6jjzruquo0u">
          <button onClick={onCancel} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors" data-id="element-3298" data-mp="2zwq8zst64ajm">
            Cancel
          </button>
          <button onClick={onConfirm} className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-[var(--brand-primary)] rounded-lg hover:bg-[var(--brand-primary-hover)] transition-colors" data-id="element-3299" data-mp="27f4drnzujmkb">
            <DownloadIcon size={14} data-id="element-3300" data-mp="mq0dxmju8iat" />
            Download CSV
          </button>
        </div>
      </motion.div>
    </div>, document.body);
}
export function Payouts() {
  const navigate = useNavigate();
  const {
    selectedCompany
  } = useCompany();
  const [currentPage, setCurrentPage] = useState(1);
  const [expandedPayouts, setExpandedPayouts] = useState<Set<string>>(new Set());
  const allPayouts = useMemo(() => getAllPayouts(), []);
  // ── Filter state ──
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [datePreset, setDatePreset] = useState<DatePreset>('last_30');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [destinationFilter, setDestinationFilter] = useState('all');
  const [currencyFilter, setCurrencyFilter] = useState('all');
  const [pendingExport, setPendingExport] = useState<ExportRequest | null>(null);
  // Distinct destinations / currencies — the corresponding filter only renders
  // when the merchant actually has more than one.
  const destinations = useMemo(() => Array.from(new Set(allPayouts.map(p => p.destinationAccount))), [allPayouts]);
  const currencies = useMemo(() => Array.from(new Set(allPayouts.map(p => p.currency))), [allPayouts]);
  const hasMultipleDestinations = destinations.length > 1;
  const hasMultipleCurrencies = currencies.length > 1;
  // Anchor relative date presets to the most recent payout so ranges stay
  // meaningful against the mock data.
  const anchorDate = useMemo(() => {
    if (allPayouts.length === 0) return new Date();
    return allPayouts.reduce((max, p) => p.effectiveDateRaw > max ? p.effectiveDateRaw : max, allPayouts[0].effectiveDateRaw);
  }, [allPayouts]);
  const dateRange = useMemo(() => resolveDateRange(datePreset, anchorDate, customStart, customEnd), [datePreset, anchorDate, customStart, customEnd]);
  // ── Filtered result set — drives BOTH the table and every export ──
  const filteredPayouts = useMemo(() => {
    const q = search.trim().toLowerCase();
    return allPayouts.filter(p => {
      if (statusFilter !== 'all' && p.status !== statusFilter) return false;
      if (hasMultipleDestinations && destinationFilter !== 'all' && p.destinationAccount !== destinationFilter) return false;
      if (hasMultipleCurrencies && currencyFilter !== 'all' && p.currency !== currencyFilter) return false;
      if (dateRange) {
        const t = p.effectiveDateRaw.getTime();
        if (t < dateRange.start.getTime() || t > dateRange.end.getTime()) return false;
      }
      if (q) {
        const haystack = [p.payoutId, ...p.transfers.map(t => t.description), ...p.transfers.map(t => t.bankReference)].join(' ').toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      return true;
    });
  }, [allPayouts, search, statusFilter, destinationFilter, currencyFilter, dateRange, hasMultipleDestinations, hasMultipleCurrencies]);
  // Reset to page 1 whenever the filtered set changes size/shape.
  useEffect(() => {
    setCurrentPage(1);
  }, [search, statusFilter, datePreset, customStart, customEnd, destinationFilter, currencyFilter]);
  const totalEntries = filteredPayouts.length;
  const totalPages = Math.max(1, Math.ceil(totalEntries / PAGE_SIZE));
  const startIndex = totalEntries === 0 ? 0 : (currentPage - 1) * PAGE_SIZE;
  const endIndex = Math.min(startIndex + PAGE_SIZE, totalEntries);
  const currentEntries = filteredPayouts.slice(startIndex, endIndex);
  // ── Build an export request from the ACTIVE filters (ignores pagination) ──
  const buildExportRequest = (type: 'summary' | 'detail'): ExportRequest => {
    const statusLabel = STATUS_OPTIONS.find(o => o.value === statusFilter)?.label ?? 'All';
    return {
      type,
      payouts: filteredPayouts,
      rangeLabel: formatRangeLabel(dateRange),
      statusLabel,
      currencyLabel: hasMultipleCurrencies ? currencyFilter === 'all' ? 'All currencies' : currencyFilter : null,
      destinationLabel: hasMultipleDestinations ? destinationFilter === 'all' ? 'All accounts' : destinationFilter : null,
      detailRowCount: type === 'detail' ? countPayoutDetailRows(filteredPayouts) : 0
    };
  };
  const runExport = () => {
    if (!pendingExport) return;
    if (pendingExport.type === 'summary') {
      downloadPayoutSummaryCsv(pendingExport.payouts, selectedCompany.name);
    } else {
      downloadPayoutDetailCsv(pendingExport.payouts);
    }
    setPendingExport(null);
  };
  const hasActiveFilters = search.trim() !== '' || statusFilter !== 'all' || datePreset !== 'last_30' || hasMultipleDestinations && destinationFilter !== 'all' || hasMultipleCurrencies && currencyFilter !== 'all';
  const clearFilters = () => {
    setSearch('');
    setStatusFilter('all');
    setDatePreset('last_30');
    setCustomStart('');
    setCustomEnd('');
    setDestinationFilter('all');
    setCurrencyFilter('all');
  };
  const last7DaysPayouts = allPayouts.filter(p => {
    const dateStr = p.effectiveDate;
    return dateStr.includes('May 7') || dateStr.includes('May 6') || dateStr.includes('May 5') || dateStr.includes('May 4') || dateStr.includes('May 3') || dateStr.includes('May 2') || dateStr.includes('May 1');
  });
  const last7Total = last7DaysPayouts.reduce((sum, p) => sum + p.grossAmount, 0);
  const last30Total = allPayouts.reduce((sum, p) => sum + p.grossAmount, 0);
  const toggleExpand = (payoutId: string) => {
    setExpandedPayouts(prev => {
      const next = new Set(prev);
      if (next.has(payoutId)) {
        next.delete(payoutId);
      } else {
        next.add(payoutId);
      }
      return next;
    });
  };
  return <div className="max-w-[1600px] mx-auto" data-id="element-3301" data-mp="2iwyhzctclp8l">
      {/* Header */}
      <div className="flex items-center justify-between mb-8" data-id="element-3302" data-mp="2iv46ep3yv9h6">
        <h1 className="text-3xl font-bold text-gray-900" data-id="element-3303" data-mp="1m7z6loxgw7c6">Payouts</h1>
        <ReportDropdown label="Export" onSummary={() => setPendingExport(buildExportRequest('summary'))} onDetail={() => setPendingExport(buildExportRequest('detail'))} data-id="element-3304" data-mp="1q89mbtb2ac48" />
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-3 gap-4 mb-6" data-id="element-3305" data-mp="1zzx93gwob4zd">
        <div className="bg-white border border-gray-200 rounded-lg p-5" data-id="element-3306" data-mp="12ji2pqbjf232">
          <div className="flex items-center justify-between mb-3" data-id="element-3307" data-mp="12h47nhguxpvi">
            <span className="text-sm text-gray-500" data-id="element-3308" data-mp="17ggwu8v7b919">Next 7 days</span>
            <CalendarIcon size={16} className="text-gray-400" data-id="element-3309" data-mp="zysie6cx8val" />
          </div>
          <div className="text-2xl font-bold text-gray-900 mb-1" data-id="element-3310" data-mp="uzpb0f9gyczg">
            {formatCurrency(allPayouts.filter(p => p.status === 'pending').reduce((sum, p) => sum + p.grossAmount, 0))}
          </div>
          <div className="text-sm text-gray-500" data-id="element-3311" data-mp="m0u4t36fd2w5">
            {allPayouts.filter(p => p.status === 'pending').length} upcoming
            payout
            {allPayouts.filter(p => p.status === 'pending').length !== 1 ? 's' : ''}{' '}
            · May 6 – May 12, 2026
          </div>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-5" data-id="element-3312" data-mp="2b1wd2j86luv">
          <div className="flex items-center justify-between mb-3" data-id="element-3313" data-mp="28xh5ofvm0fa">
            <span className="text-sm text-gray-500" data-id="element-3314" data-mp="3ue1dridv7yuz">Last 7 days</span>
            <CalendarIcon size={16} className="text-gray-400" data-id="element-3315" data-mp="3pf7ybkf0pxs6" />
          </div>
          <div className="text-2xl font-bold text-gray-900 mb-1" data-id="element-3316" data-mp="3fs403znes51o">
            {formatCurrency(last7Total)}
          </div>
          <div className="text-sm text-gray-500" data-id="element-3317" data-mp="3jtpk04yd1jld">
            {last7DaysPayouts.length} payouts · May 1 – May 7, 2026
          </div>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-5" data-id="element-3318" data-mp="35vsnf8jwpd6p">
          <div className="flex items-center justify-between mb-3" data-id="element-3319" data-mp="3atkg44sdl3by">
            <span className="text-sm text-gray-500" data-id="element-3320" data-mp="2cfvfp0osi39">Last 30 days</span>
            <CalendarIcon size={16} className="text-gray-400" data-id="element-3321" data-mp="2fbtregg58s8" />
          </div>
          <div className="text-2xl font-bold text-gray-900 mb-1" data-id="element-3322" data-mp="7b9b9o3g4l6l">
            {formatCurrency(last30Total)}
          </div>
          <div className="text-sm text-gray-500" data-id="element-3323" data-mp="3trn6oo0kn8bv">
            {allPayouts.length} payouts · Apr 7 – May 6, 2026
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border border-gray-200 rounded-lg p-4 mb-4" data-id="element-3324" data-mp="l9qr6zn40tdy">
        <div className="flex flex-col lg:flex-row lg:items-end gap-3" data-id="element-3325" data-mp="q3bpkssm7pdo">
          {/* Search */}
          <div className="flex-1 min-w-[200px]" data-id="element-3326" data-mp="2arnwxsabpc14">
            <label htmlFor="payout-search" className="block text-xs font-medium text-gray-500 mb-1.5" data-id="element-3327" data-mp="25tvvo7nsgdk9">
              Search
            </label>
            <div className="relative" data-id="element-3328" data-mp="2jn3p84vfjj4l">
              <SearchIcon size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" data-id="element-3329" data-mp="2fjfh9l2vylit" />
              <input id="payout-search" type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Payout ID, description or bank reference" className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--brand-primaryring)] focus:border-[var(--brand-primary)] transition-colors" data-id="element-3330" data-mp="1mv04z6b52hb6" />
            </div>
          </div>

          {/* Status */}
          <div className="min-w-[170px]" data-id="element-3331" data-mp="6g5n1v9dgvyu">
            <label htmlFor="payout-status" className="block text-xs font-medium text-gray-500 mb-1.5" data-id="element-3332" data-mp="6h73kf95qany">
              Status
            </label>
            <select id="payout-status" value={statusFilter} onChange={e => setStatusFilter(e.target.value as StatusFilter)} className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-[var(--brand-primaryring)] focus:border-[var(--brand-primary)] transition-colors" data-id="element-3333" data-mp="gd0nijp7h6fr">
              {STATUS_OPTIONS.map(o => <option key={o.value} value={o.value} data-id="element-3334" data-mp="z6e9hzaxy2j8">
                  {o.label}
                </option>)}
            </select>
          </div>

          {/* Effective date */}
          <div className="min-w-[160px]" data-id="element-3335" data-mp="194bwkjh2dta9">
            <label htmlFor="payout-date" className="block text-xs font-medium text-gray-500 mb-1.5" data-id="element-3336" data-mp="1d3kqboc3tr5t">
              Effective date
            </label>
            <select id="payout-date" value={datePreset} onChange={e => setDatePreset(e.target.value as DatePreset)} className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-[var(--brand-primaryring)] focus:border-[var(--brand-primary)] transition-colors" data-id="element-3337" data-mp="2tkru0vjgrn6t">
              {DATE_PRESET_OPTIONS.map(o => <option key={o.value} value={o.value} data-id="element-3338" data-mp="3caref54d0e3w">
                  {o.label}
                </option>)}
            </select>
          </div>

          {/* Destination — only when the merchant has multiple accounts */}
          {hasMultipleDestinations && <div className="min-w-[170px]" data-id="element-3339" data-mp="3uxzuvtl9cph">
              <label htmlFor="payout-destination" className="block text-xs font-medium text-gray-500 mb-1.5" data-id="element-3340" data-mp="3wsk07x1d0si">
                Destination account
              </label>
              <select id="payout-destination" value={destinationFilter} onChange={e => setDestinationFilter(e.target.value)} className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-[var(--brand-primaryring)] focus:border-[var(--brand-primary)] transition-colors" data-id="element-3341" data-mp="1td4t95edjqf6">
                <option value="all" data-id="element-3342" data-mp="19n15mn3xo9xc">All accounts</option>
                {destinations.map(d => <option key={d} value={d} data-id="element-3343" data-mp="1eoqkyey6v9r8">
                    {d}
                  </option>)}
              </select>
            </div>}

          {/* Currency — only when the merchant can receive multiple currencies */}
          {hasMultipleCurrencies && <div className="min-w-[130px]" data-id="element-3344" data-mp="2v28bdrld2oom">
              <label htmlFor="payout-currency" className="block text-xs font-medium text-gray-500 mb-1.5" data-id="element-3345" data-mp="300i8w8uk61de">
                Currency
              </label>
              <select id="payout-currency" value={currencyFilter} onChange={e => setCurrencyFilter(e.target.value)} className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-[var(--brand-primaryring)] focus:border-[var(--brand-primary)] transition-colors" data-id="element-3346" data-mp="355vu8bpoounh">
                <option value="all" data-id="element-3347" data-mp="3sx150cgrowqx">All currencies</option>
                {currencies.map(c => <option key={c} value={c} data-id="element-3348" data-mp="5qic4rx8o04f">
                    {c}
                  </option>)}
              </select>
            </div>}
        </div>

        {/* Custom range inputs */}
        {datePreset === 'custom' && <div className="flex flex-wrap items-end gap-3 mt-3" data-id="element-3349" data-mp="27in1pwr9mlbz">
            <div data-id="element-3350" data-mp="23gs0es9c47y7">
              <label htmlFor="payout-date-from" className="block text-xs font-medium text-gray-500 mb-1.5" data-id="element-3351" data-mp="23hbl2gqddafx">
                From
              </label>
              <input id="payout-date-from" type="date" value={customStart} onChange={e => setCustomStart(e.target.value)} className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--brand-primaryring)] focus:border-[var(--brand-primary)] transition-colors" data-id="element-3352" data-mp="2wz9me14t52sp" />
            </div>
            <div data-id="element-3353" data-mp="2e2gv2af7ac10">
              <label htmlFor="payout-date-to" className="block text-xs font-medium text-gray-500 mb-1.5" data-id="element-3354" data-mp="2e1yw3615ljpe">
                To
              </label>
              <input id="payout-date-to" type="date" value={customEnd} onChange={e => setCustomEnd(e.target.value)} className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--brand-primaryring)] focus:border-[var(--brand-primary)] transition-colors" data-id="element-3355" data-mp="2n3ooof6ruoor" />
            </div>
          </div>}

        {/* Active-filter summary */}
        <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100" data-id="element-3356" data-mp="2pi76pek2v19">
          <span className="text-xs text-gray-500" data-id="element-3357" data-mp="3uucaomw7e671">
            Showing {totalEntries} of {allPayouts.length} payouts ·{' '}
            {formatRangeLabel(dateRange)}
          </span>
          {hasActiveFilters && <button onClick={clearFilters} className="inline-flex items-center gap-1 text-xs font-medium text-gray-500 hover:text-gray-700 transition-colors" data-id="element-3358" data-mp="3kulbi7wp730m">
              <XIcon size={12} data-id="element-3359" data-mp="3bvzxmq268c66" />
              Clear filters
            </button>}
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-id="element-3360" data-mp="1e32oatacokz5">
        <div className="overflow-x-auto" data-id="element-3361" data-mp="1a43fgxli042f">
          <table className="w-full" data-id="element-3362" data-mp="1ykpcjdm8xpb5">
            <thead data-id="element-3363" data-mp="1ylqnzj76cdte">
              <tr className="border-b border-gray-200 bg-gray-50/50" data-id="element-3364" data-mp="1yma7v5gdxca2">
                <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap w-10" data-id="element-3365" data-mp="1tkui5039ihlc"></th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap" data-id="element-3366" data-mp="1oovszaa40vjv">
                  Payout ID
                </th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap" data-id="element-3367" data-mp="2haow5kzbic76">
                  <StatusTooltipHeader data-id="element-3368" data-mp="27lng5usrvut7" />
                </th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap" data-id="element-3369" data-mp="27k2nkaq0qlxz">
                  Effective date
                </th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap" data-id="element-3370" data-mp="302q4ddxzfet9">
                  Currency
                </th>
                <th className="text-right px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap" data-id="element-3371" data-mp="33knletw8ce6f">
                  Gross amount
                </th>
                <th className="text-right px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap" data-id="element-3372" data-mp="3ck0v9no9n4ro">
                  <span className="inline-flex items-center gap-1.5 justify-end" data-id="element-3373" data-mp="3hkg5i6d93w5f">
                    Net adjustments
                    <InfoTooltip content="The net total of fees, taxes, reserves, holds, releases and other adjustments applied to this payout." label="What are net adjustments?" align="right" data-id="element-3374" data-mp="3mihq4vqbs3j0" />
                  </span>
                </th>
                <th className="text-right px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap" data-id="element-3375" data-mp="93trzi9rj9jf">
                  <span className="inline-flex items-center gap-1.5 justify-end" data-id="element-3376" data-mp="e253ryjmbu4a">
                    Net payout
                    <InfoTooltip content="Final payout amount. Gross amount + Net adjustments." label="What is the net payout?" align="right" data-id="element-3377" data-mp="3kkyi9oea45ep" />
                  </span>
                </th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap" data-id="element-3378" data-mp="3piq7ubrgbffq">
                  Destination
                </th>
                <th className="text-center px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap" data-id="element-3379" data-mp="26ftm8husnul">
                  <span className="inline-flex items-center gap-1.5 justify-center" data-id="element-3380" data-mp="h2ezrdlekdx3">
                    Transfers
                    <InfoTooltip content="Number of transfer instructions used to submit this payout." label="What are transfers?" data-id="element-3381" data-mp="c4wohgjwxmlx" />
                  </span>
                </th>
                <th className="text-center px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap" data-id="element-3382" data-mp="tdpfkc4eb949">
                  Report
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100" data-id="element-3383" data-mp="joead0kmqoln">
              {currentEntries.length === 0 && <tr data-id="element-3384" data-mp="om7g5si0tgle">
                  <td colSpan={11} className="px-5 py-16 text-center" data-id="element-3385" data-mp="olo7l2e9g4k1">
                    <p className="text-sm font-medium text-gray-900" data-id="element-3386" data-mp="amogzp76tfl8">
                      No payouts match your filters
                    </p>
                    <p className="text-sm text-gray-500 mt-1" data-id="element-3387" data-mp="eq4gquejzw25">
                      Try adjusting the date range, status or search.
                    </p>
                    {hasActiveFilters && <button onClick={clearFilters} className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-[var(--brand-primary)] hover:underline" data-id="element-3388" data-mp="5od75egw7530">
                        Clear filters
                      </button>}
                  </td>
                </tr>}
              {currentEntries.map(payout => {
              const isExpanded = expandedPayouts.has(payout.payoutId);
              return <Fragment key={payout.payoutId} data-id="element-3389" data-mp="3i5rohsnw7i5z">
                    {/* Payout Row — clickable to navigate to detail */}
                    <tr onClick={() => navigate(`/payouts/${payout.payoutId}`)} className={`hover:bg-gray-50/50 transition-colors cursor-pointer ${isExpanded ? 'bg-gray-50/30' : ''}`} data-id="element-3390" data-mp="3d6olsevdxo16">
                      <td className="px-5 py-4" data-id="element-3391" data-mp="33e90p8oskjc2">
                        <button onClick={e => {
                      e.stopPropagation();
                      toggleExpand(payout.payoutId);
                    }} className="text-gray-400 hover:text-gray-600 transition-colors" aria-label={isExpanded ? 'Collapse transfers' : 'Expand transfers'} data-id="element-3392" data-mp="2zg1jydubvi0v">
                          <motion.div animate={{
                        rotate: isExpanded ? 90 : 0
                      }} transition={{
                        duration: 0.15
                      }} data-id="element-3393" data-mp="1euv2uwwzj3aq">
                            <ChevronRightIcon size={16} data-id="element-3394" data-mp="r50ouc0xj8ow" />
                          </motion.div>
                        </button>
                      </td>
                      <td className="px-5 py-4 text-sm text-gray-900 font-mono font-medium whitespace-nowrap" data-id="element-3395" data-mp="100i6pjs5aa3r">
                        {payout.payoutId}
                      </td>
                      <td className="px-5 py-4 whitespace-nowrap" data-id="element-3396" data-mp="7imayxg5jwki">
                        {getPayoutStatusBadge(payout.status)}
                      </td>
                      <td className="px-5 py-4 text-sm text-gray-700 whitespace-nowrap" data-id="element-3397" data-mp="1as8lz6rqb8tw">
                        {payout.effectiveDate}
                      </td>
                      <td className="px-5 py-4 text-sm text-gray-700 whitespace-nowrap" data-id="element-3398" data-mp="1kitfwgwgjp6h">
                        {payout.currency}
                      </td>
                      <td className="px-5 py-4 text-sm text-gray-900 text-right whitespace-nowrap" data-id="element-3399" data-mp="rv7bj6feogik">
                        {formatAmount(payout.grossAmount)}
                      </td>
                      <td className="px-5 py-4 text-sm text-gray-700 text-right whitespace-nowrap tabular-nums" data-id="element-3400" data-mp="myp7k8rwgm7a">
                        {formatSignedAmount(payout.netAdjustments)}
                      </td>
                      <td className="px-5 py-4 text-sm text-gray-900 font-semibold text-right whitespace-nowrap" data-id="element-3401" data-mp="2cbaq1ew7vqvb">
                        {formatAmount(payout.netAmount)}
                      </td>
                      <td className="px-5 py-4 text-sm text-gray-700 whitespace-nowrap" data-id="element-3402" data-mp="2ft84qo6zse5b">
                        {payout.destinationAccount}
                      </td>
                      <td className="px-5 py-4 text-center whitespace-nowrap" data-id="element-3403" data-mp="2ktmecao8rw5g">
                        <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-gray-100 text-xs font-medium text-gray-600" data-id="element-3404" data-mp="25wc22z3rym8o">
                          {payout.transfers.length}
                        </span>
                      </td>
                      <td className="px-5 py-4 text-center whitespace-nowrap" data-id="element-3405" data-mp="33ij88dfq8mn5">
                        <button onClick={e => {
                      e.stopPropagation();
                      downloadPayoutDetailCsv([payout]);
                    }} className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-gray-600 bg-white border border-gray-200 rounded-md hover:bg-gray-50 transition-colors" data-id="element-3406" data-mp="33kndaypd3rp0">
                          <DownloadIcon size={12} data-id="element-3407" data-mp="1m7as4zf21pgw" />
                          .csv
                        </button>
                      </td>
                    </tr>

                    {/* Transfer Sub-Rows */}
                    <AnimatePresence data-id="element-3408" data-mp="1dfsccpm0tboc">
                      {isExpanded && payout.transfers.map((transfer, idx) => <motion.tr key={transfer.transferId} onClick={e => e.stopPropagation()} initial={{
                    opacity: 0,
                    height: 0
                  }} animate={{
                    opacity: 1,
                    height: 'auto'
                  }} exit={{
                    opacity: 0,
                    height: 0
                  }} transition={{
                    duration: 0.15,
                    delay: idx * 0.03
                  }} className="bg-[var(--colors-neutral-50)] border-l-2 border-l-[var(--colors-border-default)]" data-id="element-3409" data-mp="18dtbpnho0sv3" data-mpi={idx}>
                            <td className="px-5 py-3" data-id="element-3410" data-mp="kc6ff66xpp58"></td>
                            <td className="px-5 py-3 text-sm font-mono text-[var(--colors-text-secondary)] whitespace-nowrap" data-id="element-3411" data-mp="kdrbx1ct7i5q">
                              {transfer.transferId}
                            </td>
                            <td className="px-5 py-3 whitespace-nowrap" data-id="element-3412" data-mp="ybpmrwrx8wgh">
                              {getTransferStatusBadge(transfer.status)}
                            </td>
                            <td className="px-5 py-3" data-id="element-3413" data-mp="138psu7ortz7l"></td>
                            <td className="px-5 py-3" data-id="element-3414" data-mp="30441kmz65kso"></td>
                            {/* Gross amount and net adjustments are only shown at
                             the payout level; transfer rows show net only. */}
                            <td className="px-5 py-3" data-id="element-3415" data-mp="1faso6v4uqxzx"></td>
                            <td className="px-5 py-3" data-id="element-3416" data-mp="1k3ls5gshy2ay"></td>
                            <td className="px-5 py-3 text-sm text-[var(--colors-text-primary)] font-medium text-right whitespace-nowrap" data-id="element-3417" data-mp="1k56lj332rf78">
                              {formatAmount(transfer.netAmount)}
                            </td>
                            <td className="px-5 py-3 text-xs font-mono text-[var(--colors-text-tertiary)] whitespace-nowrap" data-id="element-3418" data-mp="1yztasco9avo4">
                              {transfer.bankReference}
                            </td>
                            <td className="px-5 py-3" data-id="element-3419" data-mp="1p2yem0fgh7d0"></td>
                            <td className="px-5 py-3" data-id="element-3420" data-mp="1p4j9jr5omsbe"></td>
                          </motion.tr>)}
                    </AnimatePresence>
                  </Fragment>;
            })}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between px-5 py-4 border-t border-gray-200" data-id="element-3421" data-mp="ijqdfwlxiam4">
          <span className="text-sm text-gray-500" data-id="element-3422" data-mp="b4ep4ujk9djl">
            {totalEntries === 0 ? 'No payouts' : `Showing ${startIndex + 1} to ${endIndex} of ${totalEntries} payouts`}
          </span>
          <div className="flex items-center gap-2" data-id="element-3423" data-mp="f45jt9c7kdte">
            <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors" data-id="element-3424" data-mp="f4ouc4zj3zxe">
              Previous
            </button>
            <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors" data-id="element-3425" data-mp="3sia2rdui8xin">
              Next
            </button>
          </div>
        </div>
      </div>

      {/* Export confirmation */}
      <AnimatePresence data-id="element-3426" data-mp="1dgt26cghh7d1">
        {pendingExport && <ExportConfirmDialog request={pendingExport} onCancel={() => setPendingExport(null)} onConfirm={runExport} data-id="element-3427" data-mp="1djf7e5fwjas3" />}
      </AnimatePresence>
    </div>;
}